// offlineManager.js - Gestion du téléchargement hors-ligne

const DB_NAME = 'disney-music-offline';
const DB_VERSION = 1;
const STORE_NAME = 'downloaded-tracks';
const CACHE_NAME = 'disney-music-audio-v1';

// ===== IndexedDB: métadonnées des pistes téléchargées =====

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('album_id', 'album_id', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Sauvegarder les métadonnées d'une piste
export async function saveTrackMeta(track) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({
      ...track,
      downloaded_at: new Date().toISOString()
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Supprimer les métadonnées d'une piste
export async function removeTrackMeta(trackId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(trackId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// Récupérer toutes les pistes téléchargées
export async function getAllDownloaded() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Vérifier si une piste est téléchargée
export async function isTrackDownloaded(trackId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(trackId);
    request.onsuccess = () => resolve(!!request.result);
    request.onerror = () => reject(request.error);
  });
}

// Récupérer les pistes d'un album téléchargé
export async function getDownloadedByAlbum(albumId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const index = tx.objectStore(STORE_NAME).index('album_id');
    const request = index.getAll(albumId);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ===== Cache API: fichiers audio =====

function getAudioUrl(filePath) {
  if (!filePath) return '';
  if (filePath.startsWith('http')) return filePath;
  return `https://disney-music.gpworld.fr${filePath.startsWith('/') ? filePath : '/' + filePath}`;
}

// Télécharger un fichier audio dans le cache
export async function downloadAudio(filePath, onProgress) {
  const url = getAudioUrl(filePath);
  
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Erreur téléchargement: ${response.status}`);

  const contentLength = response.headers.get('content-length');
  const total = contentLength ? parseInt(contentLength, 10) : 0;
  let loaded = 0;

  const reader = response.body.getReader();
  const chunks = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    if (onProgress && total > 0) {
      onProgress(Math.round((loaded / total) * 100));
    }
  }

  const blob = new Blob(chunks, { type: 'audio/mpeg' });
  const cacheResponse = new Response(blob, {
    headers: { 'Content-Type': 'audio/mpeg' }
  });

  const cache = await caches.open(CACHE_NAME);
  await cache.put(url, cacheResponse);

  return url;
}

// Supprimer un fichier audio du cache
export async function removeAudio(filePath) {
  const url = getAudioUrl(filePath);
  const cache = await caches.open(CACHE_NAME);
  await cache.delete(url);
}

// Vérifier si un fichier audio est en cache
export async function isAudioCached(filePath) {
  const url = getAudioUrl(filePath);
  const cache = await caches.open(CACHE_NAME);
  const response = await cache.match(url);
  return !!response;
}

// ===== Fonctions combinées =====

// Télécharger une piste complète (audio + métadonnées)
export async function downloadTrack(track, albumInfo, onProgress) {
  await downloadAudio(track.file_path, onProgress);
  await saveTrackMeta({
    id: track.id,
    title: track.title,
    artist: track.artist,
    file_path: track.file_path,
    duration: track.duration,
    track_number: track.track_number,
    disc_number: track.disc_number,
    album_id: albumInfo?.id || track.album_id,
    album_title: albumInfo?.title || track.album_title || '',
    album_artist: albumInfo?.artist || track.album_artist || '',
    cover_url: albumInfo?.cover_url || track.cover_url || ''
  });
}

// Supprimer une piste complète
export async function removeDownloadedTrack(track) {
  await removeAudio(track.file_path);
  await removeTrackMeta(track.id);
}

// Télécharger un album complet
export async function downloadAlbum(album, tracks, onProgress) {
  const total = tracks.length;
  let completed = 0;

  for (const track of tracks) {
    await downloadTrack(track, album, (trackProgress) => {
      if (onProgress) {
        const overall = Math.round(((completed + trackProgress / 100) / total) * 100);
        onProgress(overall, completed + 1, total);
      }
    });
    completed++;
  }
}

// Supprimer un album complet
export async function removeDownloadedAlbum(albumId) {
  const tracks = await getDownloadedByAlbum(albumId);
  for (const track of tracks) {
    await removeAudio(track.file_path);
    await removeTrackMeta(track.id);
  }
}

// Calculer l'espace utilisé
export async function getStorageUsage() {
  if (navigator.storage && navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate();
    return {
      used: estimate.usage || 0,
      quota: estimate.quota || 0,
      usedMB: Math.round((estimate.usage || 0) / 1024 / 1024),
      quotaMB: Math.round((estimate.quota || 0) / 1024 / 1024)
    };
  }
  return { used: 0, quota: 0, usedMB: 0, quotaMB: 0 };
}

// Tout supprimer
export async function clearAllDownloads() {
  await caches.delete(CACHE_NAME);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
